import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { PlantIcon } from '@/components/PlantIcon';
import { Check, Lock, ChevronDown } from 'lucide-react';

interface Plant {
  id: string;
  name: string;
  category: string;
  description: string | null;
  difficulty: string | null;
  plant_type: string;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'bg-primary/20 text-primary border-primary/30',
  medium: 'bg-accent/20 text-accent-foreground border-accent/30',
  hard: 'bg-destructive/20 text-destructive border-destructive/30',
};

const CATEGORY_CONFIG: { key: string; label: string; emoji: string }[] = [
  { key: 'vegetable', label: 'Vegetables', emoji: '🥬' },
  { key: 'fruit', label: 'Fruit', emoji: '🍎' },
  { key: 'herb', label: 'Herbs', emoji: '🌿' },
  { key: 'flower', label: 'Flowers', emoji: '🌸' },
];

interface PlantSelectorProps {
  plants: Plant[];
  selectedPlants: string[];
  establishedPlants: Set<string>;
  onTogglePlant: (id: string) => void;
  onToggleEstablished: (id: string) => void;
  atLimit?: (plantId: string) => boolean;
}

export function PlantSelector({
  plants,
  selectedPlants,
  establishedPlants,
  onTogglePlant,
  onToggleEstablished,
  atLimit,
}: PlantSelectorProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {CATEGORY_CONFIG.map(({ key, label, emoji }) => {
        const categoryPlants = plants
          .filter(p => p.category === key)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (categoryPlants.length === 0) return null;

        const selectedCount = categoryPlants.filter(p => selectedPlants.includes(p.id)).length;
        const isExpanded = expandedCategories.has(key);

        return (
          <div key={key} className="rounded-xl border-2 border-border overflow-hidden">
            <button
              onClick={() => toggleCategory(key)}
              className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50"
            >
              <span className="text-lg">{emoji}</span>
              <span className="flex-1 font-semibold text-sm text-foreground">{label}</span>
              {selectedCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {selectedCount} selected
                </Badge>
              )}
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
              <div className="grid grid-cols-2 gap-2 p-3 pt-0">
                {categoryPlants.map(plant => {
                  const isSelected = selectedPlants.includes(plant.id);
                  const isPerennial = plant.plant_type === 'perennial';
                  const isEstablished = establishedPlants.has(plant.id);
                  const isAtLimit = atLimit ? atLimit(plant.id) : false;

                  return (
                    <div
                      key={plant.id}
                      className={`rounded-lg border-2 p-3 transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : isAtLimit
                          ? 'border-border opacity-50'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <button
                        onClick={() => onTogglePlant(plant.id)}
                        className="flex w-full items-center gap-2 text-left"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            <PlantIcon name={plant.name} />
                            <span className="text-sm font-semibold text-foreground">{plant.name}</span>
                          </div>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${DIFFICULTY_COLORS[plant.difficulty || 'medium']}`}
                            >
                              {plant.difficulty}
                            </Badge>
                            {isPerennial && (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-secondary/20 text-secondary-foreground border-secondary/30"
                              >
                                perennial
                              </Badge>
                            )}
                          </div>
                        </div>
                        {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                        {isAtLimit && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      </button>
                      {isSelected && isPerennial && (
                        <label
                          className="mt-2 flex items-center gap-2 cursor-pointer border-t border-border/50 pt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isEstablished}
                            onCheckedChange={() => onToggleEstablished(plant.id)}
                          />
                          <span className="text-xs text-muted-foreground">Already established</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
